import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Back, Chevron, Close } from "./Icons";

/** A customer photo, full screen, without leaving the app.
 *
 *  These used to be links with target="_blank". In a tab that is fine; from the
 *  home screen it hands the whole thing over to Safari and the way back is a
 *  trip through the app switcher.
 *
 *  It is portalled to <body> and not left where it is used. `position: fixed`
 *  is only fixed to the viewport while no ancestor has a transform, and .page
 *  animates one; inside it the overlay was laid out against the 640px column
 *  instead of the screen, which is why it opened off-centre and ran off the
 *  bottom. Out at the body there is nothing left to trap it.
 *
 *  Given the rest of the set it steps through them too, by the arrows, the
 *  arrow keys or a swipe. Looking at five photos used to be five trips through
 *  open and close. */
export default function Lightbox({
  src,
  list,
  onClose,
}: {
  src: string;
  /** Every photo it can step to, in order, `src` among them. Absent, or only
   *  the one, and there is nothing to step to and no arrows. */
  list?: string[];
  onClose: () => void;
}) {
  const all = list && list.length > 1 ? list : [src];
  const count = all.length;
  const [at, setAt] = useState(() => Math.max(0, all.indexOf(src)));
  // The list can shrink under it while it is open, when a photo is cut.
  const shown = all[Math.min(at, count - 1)] ?? src;
  const touch = useRef<{ x: number; y: number } | null>(null);

  // Round the ends rather than stopping at them: with a handful of photos, the
  // first one after the last is a tap instead of four. With one it goes nowhere.
  const step = (by: number) => setAt((now) => (Math.min(now, count - 1) + by + count) % count);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "ArrowRight") step(1);
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose, count]);

  return createPortal(
    <div
      className="lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      onTouchStart={(e) => {
        const finger = e.touches[0];
        touch.current = finger ? { x: finger.clientX, y: finger.clientY } : null;
      }}
      onTouchEnd={(e) => {
        const from = touch.current;
        const finger = e.changedTouches[0];
        touch.current = null;
        if (!from || !finger || count < 2) return;
        const dx = finger.clientX - from.x;
        const dy = finger.clientY - from.y;
        // Sideways and far enough to mean it. A finger that moved fires no
        // click, so a swipe never closes it and a tap still does.
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
      }}
    >
      <button className="lightbox-close" type="button" aria-label="Close" onClick={onClose}>
        <Close />
      </button>
      <img src={shown} alt="" />
      {count > 1 && (
        <>
          <span className="lightbox-count">
            {Math.min(at, count - 1) + 1} / {count}
          </span>
          <button
            className="lightbox-nav lightbox-prev"
            type="button"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
          >
            <Back size={24} />
          </button>
          <button
            className="lightbox-nav lightbox-next"
            type="button"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
          >
            <Chevron size={24} />
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
